import React, { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import {
    UIKitInput,
    UIKitButton,
    UIKitTextarea,
    UIKitInputPhoneMask,
} from '@kespa/ui';

import { ErrorInform, SuccessInform } from '../FormSection';

const FormTitle = styled.div`
    font-family: 'Futura PT', sans-serif;
    font-size: 48px;
    line-height: 100%;
    margin-bottom: 16px;
    color: #000;
`;

const Column = styled.div`
    margin: 0 15px;
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    order: 1;
    form {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
    }
    @media screen and (max-width: 600px) {
        margin: 0;
    }
`;

const SendInformWrap = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 100%;
    justify-content: center;
    h2 {
        font-family: 'Futura PT', sans-serif;
        font-size: 44px;
        line-height: 54px;
        margin-bottom: 20px;
    }
    img {
        width: 150px;
        margin-bottom: 30px;
    }
    p {
        font-family: 'Circe Web', sans-serif;
        font-size: 18px;
    }
    @media screen and (max-width: 800px) {
        img {
            width: 70px;
        }
        h2 {
            font-size: 20px;
            line-height: 30px;
        }
        p {
            font-size: 14px;
        }
    }
`;

const StyledForm = styled.form`
    display: flex;
    flex-direction: column;
    width: 100%;

    font-family: 'Circe Web', sans-serif;
    font-size: 18px;
    color: #000;
    /* margin-top: 16px; */

    textarea {
        font-family: 'Circe Web', sans-serif;
    }
    input {
        font-family: 'Circe Web', sans-serif;
    }
    button {
        font-family: 'Circe Web', sans-serif;
    }
`;

const StyledIntroText = styled.div`
    margin-bottom: 24px;
`;

const StyledPrivacyPolicyText = styled.div`
    margin-top: 15px;
    margin-bottom: 15px;
    font-size: 0.7em;
`;

const InputErrorStyled = styled.span`
    color: red;
`;

export const SignupForm = ({
    submitURL = '/api/auth/signup',
    defaultValues,
    showBio,
    email = true,
    password,
    onSuccess,
    header = 'Запишитесь',
    description = 'на бесплатный вводный урок. Мы расскажем вам о методике КЭСПА, определим ваш уровень и языковые особенности.',
}) => {
    const [status, setStatus] = useState('none');
    const [fields, setFields] = useState(() => {
        const initialFields = [
            {
                name: 'firstName',
                label: 'Имя',
                placeholder: 'Введите имя',
                value: '',
                error: null,
            },
        ];

        if (email) {
            initialFields.push({
                name: 'email',
                label: 'Почта',
                placeholder: 'Введите почту',
                value: '',
                error: null,
            });
        }

        if (password) {
            initialFields.push({
                name: 'password',
                label: 'Пароль',
                placeholder: 'Придумайте пароль',
                value: '',
                error: null,
            });
        }

        initialFields.push({
            name: 'phone',
            label: 'Телефон',
            placeholder: 'Введите телефон',
            value: '',
            error: null,
        });

        if (showBio) {
            initialFields.push({
                name: 'bio',
                label: 'Дополнительная информация',
                placeholder: 'Расскажите подробнее о своих потребностях',
                value: '',
                error: null,
            });
        }

        return initialFields;
    });

    useEffect(() => {
        if (status === 'submitting') {
            let check = true;

            const submit = async () => {
                const fieldsValues = {
                    ...defaultValues,
                };

                for (const { name, value } of fields) {
                    fieldsValues[name] = value;
                }

                try {
                    const response = await fetch(submitURL, {
                        method: 'POST',
                        body: JSON.stringify(fieldsValues),
                        headers: {
                            'Content-Type': 'application/json',
                        },
                    });

                    const responseBody = await response.json();

                    if (!check) {
                        return;
                    }

                    if (
                        response.status === 400 &&
                        responseBody &&
                        Array.isArray(responseBody.errors)
                    ) {
                        const { errors } = responseBody;
                        const validationErrors = {};
                        for (const error of errors) {
                            if (error.name === 'ValidationError') {
                                validationErrors[error.key] = error.message;
                            }

                            if (
                                error.name === 'UserError' &&
                                error.message === 'INVALID_PROMOCODE'
                            ) {
                                validationErrors.promoCode =
                                    'Неверный промокод';
                            }
                        }
                        setStatus('none');

                        setFields(prevState => {
                            return prevState.map(field => {
                                if (
                                    typeof validationErrors[field.name] ===
                                    'string'
                                ) {
                                    return {
                                        ...field,
                                        error: validationErrors[field.name],
                                    };
                                }
                                return field;
                            });
                        });
                    } else if (response.status === 200) {
                        setStatus('success');
                    } else {
                        throw new Error();
                    }
                } catch (error) {
                    if (check) {
                        setStatus('error');
                    }
                }
            };

            submit();

            return () => {
                check = false;
            };
        }
    }, [status, fields, submitURL]);

    useEffect(() => {
        try {
            const COOKIE_NAME = 'kespa-signup-query';
            const hasCookie = document.cookie
                .split(';')
                .some(item => item.trim().startsWith(`${COOKIE_NAME}=`));

            const search = window.location.search.slice(1);

            if (search !== '' && !hasCookie) {
                const date = new Date();
                date.setTime(date.getTime() + 365 * 24 * 60 * 60 * 1000);
                const dateString = date.toGMTString();
                const cookieValue = `${COOKIE_NAME}=${encodeURIComponent(
                    search
                )}; path=/; expires=${dateString}`;

                document.cookie = cookieValue;
            }
        } catch (error) {
            console.error(error);
        }
    }, []);

    useEffect(() => {
        if (status === 'success') {
            try {
                window.dataLayer.push({
                    event: 'SubmitApplication',
                });
            } catch (error) {
                console.error(error);
            }
        }
    }, [status]);

    const onChange = useCallback(e => {
        const { value, name } = e.currentTarget;
        setFields(prevState => {
            return prevState.map(field => {
                if (field.name === name) {
                    return {
                        ...field,
                        error: null,
                        value,
                    };
                }

                return field;
            });
        });
    }, []);

    const onChangePhone = useCallback(value => {
        setFields(prevState => {
            return prevState.map(field => {
                if (field.name === 'phone') {
                    return {
                        ...field,
                        error: null,
                        value,
                    };
                }

                return field;
            });
        });
    });

    const onSubmit = useCallback(async e => {
        e.preventDefault();
        setStatus('submitting');
    }, []);

    useEffect(() => {
        if (status === 'success' && onSuccess) {
            onSuccess();
        }
    }, [onSuccess, status]);

    if (status === 'success') {
        return <SuccessInform />;
    }

    if (status === 'error') {
        return <ErrorInform />;
    }

    const isSubmitting = status === 'submitting';

    return (
        <StyledForm onSubmit={onSubmit}>
            <FormTitle>{header}</FormTitle>
            <StyledIntroText>{description}</StyledIntroText>
            {fields.map(({ name, label, value, error, placeholder }) => {
                const hasError = typeof error === 'string';
                return (
                    <React.Fragment key={name}>
                        {name === 'bio' ? (
                            <UIKitTextarea
                                value={value}
                                error={hasError}
                                name={name}
                                onChange={onChange}
                                placeholder={placeholder}
                                label={label}
                                disabled={isSubmitting}
                            />
                        ) : (
                            <UIKitInput
                                value={value}
                                type={
                                    name === 'password' ? 'password' : undefined
                                }
                                error={hasError}
                                name={name}
                                onChange={
                                    name === 'phone' ? onChangePhone : onChange
                                }
                                placeholder={placeholder}
                                label={label}
                                disabled={isSubmitting}
                                errorMessage={error}
                                customInputComponent={
                                    name === 'phone'
                                        ? UIKitInputPhoneMask
                                        : undefined
                                }
                            />
                        )}
                    </React.Fragment>
                );
            })}

            <StyledPrivacyPolicyText>
                Нажимая «Записаться», вы принимаете условия{' '}
                <a href="/privacy">
                    Соглашения на обработку персональных данных.
                </a>
            </StyledPrivacyPolicyText>

            <UIKitButton
                type="submit"
                fullWidth
                size="M"
                disabled={isSubmitting}
            >
                {isSubmitting ? 'Отправка' : 'Записаться'}
            </UIKitButton>
        </StyledForm>
    );
};
